// OWNER: AREA-OPS (dashboard, applications/*, suppressions, audit, system). Components go in src/features/ops.
// Overview tab: setup checklist, health for the application, recent messages,
// properties, configuration counts, and a quick-start request.
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Inbox } from 'lucide-react'
import type { ReactNode } from 'react'
import { applicationQuery } from '@/api/queries/applications'
import { statsOverviewQuery } from '@/api/queries/stats'
import type { StatsRange } from '@/api/types'
import { EmptyState } from '@/components/EmptyState'
import { KeyValueList } from '@/components/KeyValueList'
import { ErrorState } from '@/components/QueryState'
import { RelativeTime } from '@/components/RelativeTime'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Segmented } from '@/components/ui/segmented'
import { Skeleton, SkeletonText } from '@/components/ui/skeleton'
import { appEventTypesQuery, recentMessagesQuery } from '@/features/ops/api'
import { MessageRows } from '@/features/ops/MessageRows'
import { GoingLiveChecklist } from '@/features/ops/GoingLive'
import { IncludeTestSwitch } from '@/features/ops/IncludeTestSwitch'
import { LiveSendingBadge } from '@/features/ops/LiveSending'
import { QuickStart, SetupChecklist, setupSteps } from '@/features/ops/QuickStart'
import { describeTestMode, testModeOf } from '@/features/ops/test-mode'
import { StatLedger } from '@/features/ops/StatFigures'
import { VolumeChart } from '@/features/ops/VolumeChart'
import { formatNumber } from '@/lib/format'

export const Route = createFileRoute('/applications/$appId/')({
  validateSearch: (s: Record<string, unknown>): { range?: StatsRange; include_test?: true } => ({
    range: s.range === '24h' || s.range === '30d' || s.range === '7d' ? s.range : undefined,
    // Test traffic is left out by default, as on the dashboard.
    include_test: s.include_test === true || s.include_test === 'true' ? true : undefined,
  }),
  component: ApplicationOverview,
})

const RANGES = [
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
] as const satisfies readonly { value: StatsRange; label: string }[]

function ApplicationOverview() {
  const { appId } = Route.useParams()
  const { range = '7d', include_test: includeTest = false } = Route.useSearch()
  const navigate = Route.useNavigate()
  const app = useQuery(applicationQuery(appId))
  const stats = useQuery(statsOverviewQuery({ app: appId, range, includeTest }))
  const recent = useQuery(recentMessagesQuery(appId))
  const eventTypes = useQuery(appEventTypesQuery(appId))
  const a = app.data

  return (
    <div className="space-y-5">
      {/* Setup first; once it's done, what's left before live mail. */}
      {a &&
        eventTypes.data &&
        (setupSteps(a, eventTypes.data).every((s) => s.done) ? (
          <GoingLiveChecklist app={a} />
        ) : (
          <SetupChecklist app={a} eventTypes={eventTypes.data} />
        ))}

      <section aria-label="Health" className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-ink">Health</h2>
          <div className="flex flex-wrap items-center gap-4">
            <IncludeTestSwitch
              checked={includeTest}
              onCheckedChange={(on) =>
                void navigate({
                  search: (prev) => ({ ...prev, include_test: on ? true : undefined }),
                  replace: true,
                })
              }
            />
            <Segmented
              aria-label="Time range"
              value={range}
              onValueChange={(r) =>
                void navigate({
                  search: (prev) => ({ ...prev, range: r === '7d' ? undefined : r }),
                  replace: true,
                })
              }
              options={RANGES}
            />
          </div>
        </div>
        {stats.isError ? (
          <Card>
            <ErrorState compact error={stats.error} onRetry={() => void stats.refetch()} />
          </Card>
        ) : (
          <>
            <StatLedger stats={stats.data} compact />
            <div className="grid grid-cols-[minmax(0,1fr)] gap-5 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
              <Card>
                <CardHeader
                  title="Volume by outcome"
                  description={
                    includeTest
                      ? 'Messages accepted in each period, by current status. Includes test messages.'
                      : 'Messages accepted in each period, by current status. Test messages aren’t counted.'
                  }
                />
                <CardBody>
                  {stats.data ? (
                    <VolumeChart
                      series={stats.data.series}
                      range={range}
                      showCaptured={includeTest}
                    />
                  ) : (
                    <Skeleton className="h-64" />
                  )}
                </CardBody>
              </Card>
              <Card>
                <CardHeader
                  title="Recent messages"
                  actions={
                    <Link
                      to="/messages"
                      search={{ app: appId }}
                      className="text-xs font-medium text-brass hover:underline"
                    >
                      Open message log
                    </Link>
                  }
                />
                {recent.isPending ? (
                  <CardBody>
                    <SkeletonText lines={5} />
                  </CardBody>
                ) : recent.isError ? (
                  <ErrorState compact error={recent.error} onRetry={() => void recent.refetch()} />
                ) : recent.data.length === 0 ? (
                  <EmptyState
                    compact
                    icon={Inbox}
                    title="No messages yet"
                    description="Messages appear here as soon as this application posts its first event."
                  />
                ) : (
                  <MessageRows messages={recent.data.slice(0, 5)} />
                )}
              </Card>
            </div>
          </>
        )}
      </section>

      <div className="grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader title="Details" />
          <CardBody>
            {!a ? (
              <SkeletonText lines={5} />
            ) : (
              <KeyValueList
                items={[
                  { label: 'Slug', value: <span className="font-mono">{a.slug}</span> },
                  { label: 'Owning team', value: a.owner_team },
                  { label: 'Contact', value: a.owner_email },
                  { label: 'Retention', value: `${a.settings?.retention_days ?? 30} days` },
                  {
                    label: 'Rate limit',
                    value: `${formatNumber(a.settings?.rate_limit_per_minute ?? 600)} per minute`,
                  },
                  {
                    label: 'Live sending',
                    value: <LiveSendingBadge app={a} />,
                  },
                  { label: 'Test mode', value: describeTestMode(testModeOf(a.settings)) },
                  { label: 'Created', value: <RelativeTime value={a.created_at} /> },
                  {
                    label: 'Updated',
                    value: (
                      <>
                        <RelativeTime value={a.updated_at} />{' '}
                        <span className="text-ink-3">by {a.updated_by}</span>
                      </>
                    ),
                  },
                ]}
              />
            )}
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Configuration" />
          <CardBody>
            {!a ? (
              <SkeletonText lines={5} />
            ) : (
              <KeyValueList
                items={[
                  {
                    label: 'Event types',
                    value: (
                      <TabCount
                        to="/applications/$appId/event-types"
                        appId={appId}
                        n={a.counts.event_types}
                      />
                    ),
                  },
                  {
                    label: 'Templates',
                    value: (
                      <TabCount
                        to="/applications/$appId/templates"
                        appId={appId}
                        n={a.counts.templates}
                      />
                    ),
                  },
                  {
                    label: 'Senders',
                    value: (
                      <TabCount
                        to="/applications/$appId/senders"
                        appId={appId}
                        n={a.counts.senders}
                      />
                    ),
                  },
                  {
                    label: 'Active API keys',
                    value: (
                      <TabCount
                        to="/applications/$appId/keys"
                        appId={appId}
                        n={a.counts.active_api_keys}
                      />
                    ),
                  },
                  {
                    label: 'Workload identities',
                    value: (
                      <TabCount
                        to="/applications/$appId/settings"
                        appId={appId}
                        n={a.counts.workload_identities}
                      />
                    ),
                  },
                  {
                    label: 'Webhooks',
                    value: (
                      <TabCount
                        to="/applications/$appId/webhooks"
                        appId={appId}
                        n={a.counts.webhooks}
                      />
                    ),
                  },
                  {
                    label: 'Variables',
                    value: (
                      <TabCount
                        to="/applications/$appId/variables"
                        appId={appId}
                        n={Object.keys(a.variables ?? {}).length}
                      />
                    ),
                  },
                  { label: 'Messages, last 24 hours', value: formatNumber(a.counts.messages_24h) },
                ]}
              />
            )}
          </CardBody>
        </Card>
      </div>

      <QuickStart eventTypes={eventTypes.data} />
    </div>
  )
}

function TabCount({
  to,
  appId,
  n,
}: {
  to:
    | '/applications/$appId/event-types'
    | '/applications/$appId/templates'
    | '/applications/$appId/senders'
    | '/applications/$appId/keys'
    | '/applications/$appId/settings'
    | '/applications/$appId/webhooks'
    | '/applications/$appId/variables'
  appId: string
  n: number
}): ReactNode {
  return (
    <Link to={to} params={{ appId }} className="tabular text-ink hover:text-brass hover:underline">
      {formatNumber(n)}
    </Link>
  )
}
